import { NgClass, TitleCasePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { getRoleIcon } from '@app/v2/settings/components/manage-team/constants/role-icons'
import { getStatusDropdownOptions, USER_STATUS_ACTION } from '@app/v2/settings/components/manage-team/constants/status-dropdown-options'
import { INVITATION_STATUS } from '@app/v2/settings/enums/invitation-status.enum'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { TruncationDetectorDirective } from '@app/v2/shared/directives/truncation-detector.directive'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { OrganizationUser, USER_ROLES } from '@app/v2/shared/types/user.type'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { getRolesDropdownOptions, MANAGE_TEAM_ASSIGNABLE_ROLES } from '../../constants/roles-dropdown-options'

@Component({
  selector: 'app-user-card',
  imports: [SafeDomPipe, NgClass, NgxTippyModule, TruncationDetectorDirective, DropdownComponent, TitleCasePipe],
  standalone: true,
  templateUrl: './user-card.component.html',
  styleUrl: './user-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserCardComponent {
  public user = input.required<OrganizationUser>()
  public isStatusActionsDisabled = input<boolean>(false)
  public statusActionSelected = output<{ user: OrganizationUser; action: USER_STATUS_ACTION }>()
  public updateTeamMemberRole = output<{ user: OrganizationUser; role: USER_ROLES }>()

  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _currentUserRole = toSignal(this._workspaceRoleService.getUserRole(), { initialValue: null as string | null })

  public readonly roleLabel = computed(() => this._formatRole(this.user().role))
  public readonly roleIcon = computed(() => getRoleIcon(this.user().role, this.user().id))
  public readonly statusLabel = computed(() => this._formatStatus(this.user().status))
  public readonly statusClasses = computed(() => this._getStatusClasses(this.user().status))
  public readonly statusTextColorClass = computed(() => this._getStatusTextColorClass(this.user().status))
  public readonly lastSeen = computed(() => this.user().last_seen || '—————')
  public readonly hasFullname = computed(() => !!this.user().fullname?.trim())
  public readonly hasProfilePicture = computed(() => !!this.user().profile_picture?.trim())
  public readonly avatarSrc = computed(() => this.user().profile_picture)
  public readonly avatarInitials = computed(() => this._getInitials(this.user().fullname, this.user().email))
  public readonly isOwner = computed(() => this.user().role === USER_ROLES.OWNER)
  public readonly isCurrentUser = computed(() => this.user().user_id === this._workspaceRoleService.getCurrentUserId())
  public readonly isActiveMember = computed(() => this.user().status === INVITATION_STATUS.ACTIVE)
  public readonly canChangeTargetUser = computed(() => this._canChangeTargetUser())
  public readonly canDeleteTargetUser = computed(() => this._canDeleteTargetUser())
  public readonly statusDropdownOptions = computed(() =>
    getStatusDropdownOptions(this.user().status, {
      canChange: this.canChangeTargetUser(),
      canDelete: this.canDeleteTargetUser()
    })
  )
  public readonly roleDropdownOptions = computed(() => {
    const actorRole = this._currentUserRole()
    const canChange = this.canChangeTargetUser() && this.isActiveMember()

    if (!canChange || !actorRole) {
      return getRolesDropdownOptions({ canChange: false, assignableRoles: [] })
    }

    const manageTeamRoles = new Set<string>(MANAGE_TEAM_ASSIGNABLE_ROLES)
    const assignableRoles = MANAGE_TEAM_ASSIGNABLE_ROLES.filter(
      (role): role is USER_ROLES => role !== this.user().role && manageTeamRoles.has(role) && this._workspaceRoleService.canAssignRole(this.user().role, role, actorRole)
    )

    return getRolesDropdownOptions({ canChange: true, assignableRoles })
  })
  public readonly shouldShowRoleDropdown = computed(() => !this.isOwner() && !this.isCurrentUser() && this.isActiveMember() && this.roleDropdownOptions().length > 0)
  public readonly isStatusDropdownDisabled = computed(() => this.isStatusActionsDisabled() || this.isOwner() || this.isCurrentUser() || this.statusDropdownOptions().length === 0)
  public readonly isRoleDropdownDisabled = computed(() => this.isStatusActionsDisabled() || !this.shouldShowRoleDropdown())
  public readonly showStatusDropdownChevron = computed(() => !this.isOwner() && !this.isCurrentUser() && this.statusDropdownOptions().length > 0)
  public readonly showRoleDropdownChevron = computed(() => this.shouldShowRoleDropdown())

  /**
   * @description Handles status dropdown option selection
   * @param option DropdownOption
   * @returns void
   */
  public onStatusOptionClicked(option: DropdownOption): void {
    this.statusActionSelected.emit({ user: this.user(), action: option.key as USER_STATUS_ACTION })
  }

  /**
   * @description Forward team member role update from a user card
   * @param event { user: OrganizationUser; role: USER_ROLES }
   * @returns void
   */
  public onUpdateTeamMemberRole(event: { user: OrganizationUser; role: USER_ROLES }): void {
    this.updateTeamMemberRole.emit(event)
  }

  /**
   * @description Handles role dropdown option selection
   * @param option DropdownOption
   * @returns void
   */
  public onRoleOptionClicked(option: DropdownOption): void {
    this.onUpdateTeamMemberRole({
      user: this.user(),
      role: option.key as USER_ROLES
    })
  }

  /**
   * @description Checks if the current user can change the target user's status
   * @returns boolean
   */
  private _canChangeTargetUser(): boolean {
    if (this.isCurrentUser()) return false
    const actorRole = this._currentUserRole()
    if (!actorRole) return false
    return this._workspaceRoleService.canChangeUser(this.user().role, actorRole)
  }

  /**
   * @description Checks if the current user can delete the target user
   * @returns boolean
   */
  private _canDeleteTargetUser(): boolean {
    if (this.isCurrentUser()) return false
    const actorRole = this._currentUserRole()
    if (!actorRole) return false
    return this._workspaceRoleService.canDeleteUser(this.user().role, actorRole)
  }

  /**
   * @description Gets avatar initials from full name, falling back to email
   * @param fullname string
   * @param email string
   * @returns string
   */
  private _getInitials(fullname: string, email: string): string {
    if (fullname?.trim()) {
      return fullname
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }

    if (email?.trim()) {
      return email.trim().charAt(0).toUpperCase()
    }

    return '??'
  }

  private _formatRole(role: USER_ROLES): string {
    return role.charAt(0) + role.slice(1).toLowerCase()
  }

  /**
   * @description Formats the status label
   * @param status INVITATION_STATUS
   * @returns string
   */
  private _formatStatus(status: INVITATION_STATUS): string {
    return status.charAt(0).toUpperCase() + status.slice(1)
  }

  /**
   * @description Formats the status text color class
   * @param status INVITATION_STATUS
   * @returns string
   */
  private _getStatusTextColorClass(status: INVITATION_STATUS): string {
    switch (status) {
      case INVITATION_STATUS.ACTIVE:
        return 'tw-text-green-800'
      case INVITATION_STATUS.PENDING:
        return 'tw-text-yellow-800'
      case INVITATION_STATUS.SUSPENDED:
        return 'tw-text-gray-900'
      case INVITATION_STATUS.EXPIRED:
      default:
        return 'tw-text-red-800'
    }
  }

  /**
   * @description Formats the status classes
   * @param status INVITATION_STATUS
   * @returns string
   */
  private _getStatusClasses(status: INVITATION_STATUS): string {
    const textColor = this._getStatusTextColorClass(status)

    switch (status) {
      case INVITATION_STATUS.ACTIVE:
        return `tw-border-green-300 tw-bg-green-100 ${textColor}`
      case INVITATION_STATUS.PENDING:
        return `tw-border-yellow-300 tw-bg-yellow-100 ${textColor}`
      case INVITATION_STATUS.SUSPENDED:
        return `tw-border-gray-300 tw-bg-gray-300 ${textColor}`
      case INVITATION_STATUS.EXPIRED:
      default:
        return `tw-border-red-300 tw-bg-red-100 ${textColor}`
    }
  }
}
